import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PlusOutlined,
  StarOutlined,
  ThunderboltOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Button, Card, Col, Layout, message, Row, Space, Spin, Tooltip, Typography } from "antd";
import AppLayout from "@/Components/Common/AppLayout";
import { useAuthenticatedFetch } from "@/Api/Axios";
import { LoadingContext } from "@/Context/LoadingContext";
import { UserContext } from "@/Context/UserContext";

const { Title, Text } = Typography;
const { Content } = Layout;

const ResumeChoice = () => {
  const navigate = useNavigate();
  const fetch = useAuthenticatedFetch();
  const { startLoading, stopLoading } = useContext(LoadingContext);
  const { userProfileData } = useContext(UserContext);
  const [planInfo, setPlanInfo] = useState({
    plan: "free",
    maxResumes: 3,
    currentResumeCount: 0,
    canCreateResume: true,
  });
  const [checkingPlan, setCheckingPlan] = useState(true);

  // Auth guard - redirect to login if not authenticated
  useEffect(() => {
    const authToken = localStorage.getItem("authorization");
    const userId = localStorage.getItem("userId");
    if (!authToken || !userId) {
      navigate("/app/login?returnUrl=" + encodeURIComponent(window.location.pathname));
    }
  }, [navigate]);

  // Fetch plan info to check if user can create resume
  useEffect(() => {
    const checkPlanInfo = async () => {
      try {
        setCheckingPlan(true);
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const response = await fetch.get(`/user/dashboard/${userId}`, false);
        if (response.success && response.data.planInfo) {
          setPlanInfo(response.data.planInfo);
          // If user can't create resume, redirect to dashboard
          if (!response.data.planInfo.canCreateResume) {
            message.warning("You have reached your resume limit. Free plan allows only 3 resumes.");
            navigate("/app/dashboard");
          }
        }
      } catch (error) {
        console.error("Error checking plan info:", error);
      } finally {
        setCheckingPlan(false);
      }
    };
    checkPlanInfo();
  }, []);

  const handleEnhanceExisting = () => {
    // Check if user can create resume
    if (!planInfo.canCreateResume) {
      return;
    }
    // Track analytics for choice selection
    if (typeof gtag !== "undefined") {
      gtag("event", "resume_choice_selected", {
        choice_type: "enhance_existing",
      });
    }
    navigate("/app/resume-upload");
  };

  const handleCreateNew = () => {
    // Check if user can create resume
    if (!planInfo.canCreateResume) {
      return;
    }
    // Track analytics for choice selection
    if (typeof gtag !== "undefined") {
      gtag("event", "resume_choice_selected", {
        choice_type: "create_new",
      });
    }
    navigate("/app/resume-setup");
  };

  const handleResumeScore = () => {
    // Track analytics for choice selection
    if (typeof gtag !== "undefined") {
      gtag("event", "resume_choice_selected", {
        choice_type: "five_second_score",
      });
    }
    navigate("/app/resume-score");
  };

  const handleNaukriResume = () => {
    // Track analytics for choice selection
    if (typeof gtag !== "undefined") {
      gtag("event", "resume_choice_selected", {
        choice_type: "naukri_job_resume",
      });
    }
    navigate("/app/naukri-job-input");
  };

  // Show loading while checking plan
  if (checkingPlan) {
    return (
      <AppLayout>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <Spin size="large" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <style>{`
        /* CSS Reset to prevent page stretching and maintain sticky positioning */
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
          height: 100% !important;
          width: 100% !important;
          overflow-x: clip !important;
          overflow-y: auto !important;
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
        }

        *, *::before, *::after {
          box-sizing: border-box !important;
        }

        .ant-layout, .ant-layout-content {
          margin: 0 !important;
          padding: 0 !important;
          position: relative !important;
        }

        .ant-layout {
          overflow: visible !important;
        }

        body {
          user-select: none !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .resume-choice-card {
            margin-bottom: 16px !important;
          }

          .resume-choice-card .ant-card-body {
            padding: 20px 16px !important;
          }
        }
      `}</style>
      <Content
        style={{
          padding: "clamp(16px, 5vw, 24px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <div
          style={{
            marginTop: "clamp(20px, 8vw, 40px)",
            marginBottom: "clamp(16px, 5vw, 32px)",
            textAlign: "center",
            maxWidth: "700px",
            padding: "0 16px",
          }}
        >
          <Title
            level={2}
            style={{ margin: 0, fontSize: "clamp(1.5rem, 5vw, 2rem)", fontWeight: "600", color: "#333" }}
          >
            How would you like to build your resume?
          </Title>
        </div>

        {/* 5 Second Score Card - Compact Version at Top */}

        <div
          style={{ width: "100%", maxWidth: "800px", marginBottom: "clamp(16px, 5vw, 32px)", padding: "0 16px" }}
        >
          <Card
            hoverable
            style={{
              borderRadius: "12px",
              border: "2px solid #722ed1",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              transition: "all 0.3s ease",
              cursor: "pointer",
              position: "relative",
              overflow: "visible",
            }}
            styles={{
              body: {
                padding: "clamp(12px, 3vw, 16px) clamp(16px, 4vw, 24px)",
                color: "white",
              },
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px) scale(1.01)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(114, 46, 209, 0.25)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
            }}
            onClick={handleResumeScore}
          >
            <div
              style={{
                position: "absolute",
                top: "-10px",
                right: "16px",
                backgroundColor: "#ff4d4f",
                color: "white",
                padding: "4px 12px",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: "600",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                zIndex: 1,
              }}
            >
              🔥 NEW
            </div>
            <Row align="middle" gutter={16}>
              <Col xs={24} md={3}>
                <div
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    borderRadius: "50%",
                    width: "48px",
                    height: "48px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto",
                  }}
                >
                  <ThunderboltOutlined style={{ fontSize: "24px", color: "white" }} />
                </div>
              </Col>

              <Col xs={24} md={17}>
                <Title level={4} style={{ color: "white", margin: 0 }}>
                  Get Your 5-Second Resume Score
                </Title>
                <Text
                  style={{
                    fontSize: "14px",
                    color: "rgba(255, 255, 255, 0.9)",
                    lineHeight: "1.4",
                  }}
                >
                  Instant AI analysis • ATS check • Actionable improvements
                </Text>
              </Col>

              <Col xs={24} md={4} style={{ textAlign: "center" }}>
                <Button
                  type="default"
                  size="middle"
                  icon={<ThunderboltOutlined />}
                  style={{
                    borderRadius: "6px",
                    fontWeight: "600",
                    backgroundColor: "white",
                    color: "#722ed1",
                    border: "none",
                  }}
                >
                  Try Now
                </Button>
              </Col>
            </Row>
          </Card>
        </div>

        {/* Naukri Job Resume Card - Similar to 5 Second Score */}
        <div
          style={{ width: "100%", maxWidth: "800px", marginBottom: "clamp(16px, 5vw, 32px)", padding: "0 16px" }}
        >
          <Card
            hoverable
            style={{
              borderRadius: "12px",
              border: "2px solid #13c2c2",
              background: "linear-gradient(135deg, #13c2c2 0%, #08979c 100%)",
              transition: "all 0.3s ease",
              cursor: "pointer",
              position: "relative",
              overflow: "visible",
            }}
            styles={{
              body: {
                padding: "clamp(12px, 3vw, 16px) clamp(16px, 4vw, 24px)",
                color: "white",
              },
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px) scale(1.01)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(19, 194, 194, 0.25)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
            }}
            onClick={handleNaukriResume}
          >
            <div
              style={{
                position: "absolute",
                top: "-10px",
                right: "16px",
                backgroundColor: "#ff4d4f",
                color: "white",
                padding: "4px 12px",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: "600",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                zIndex: 1,
              }}
            >
              🔥 NEW
            </div>
            <Row align="middle" gutter={16}>
              <Col xs={24} md={3}>
                <div
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    borderRadius: "50%",
                    width: "48px",
                    height: "48px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto",
                  }}
                >
                  <StarOutlined style={{ fontSize: "24px", color: "white" }} />
                </div>
              </Col>

              <Col xs={24} md={17}>
                <Title level={4} style={{ color: "white", margin: 0 }}>
                  Create Resume for Naukri Job Post
                </Title>
                <Text
                  style={{
                    fontSize: "14px",
                    color: "rgba(255, 255, 255, 0.9)",
                    lineHeight: "1.4",
                  }}
                >
                  AI-powered tailored resume • Perfect ATS match • Job-specific optimization
                </Text>
              </Col>

              <Col xs={24} md={4} style={{ textAlign: "center" }}>
                <Button
                  type="default"
                  size="middle"
                  icon={<StarOutlined />}
                  style={{
                    borderRadius: "6px",
                    fontWeight: "600",
                    backgroundColor: "white",
                    color: "#13c2c2",
                    border: "none",
                  }}
                >
                  Try Now
                </Button>
              </Col>
            </Row>
          </Card>
        </div>

        <Row gutter={[16, 16]} style={{ width: "100%", maxWidth: "800px", padding: "0 16px" }}>
          {/* Enhance Existing Resume Card */}
          <Col xs={24} sm={24} md={12}>
            <Tooltip title={!planInfo.canCreateResume ? "You have reached your resume limit" : ""}>
              <Card
                hoverable={planInfo.canCreateResume}
                className="resume-choice-card enhance-card"
                style={{
                  height: "100%",
                  borderRadius: "16px",
                  border: "2px solid #e8e8e8",
                  transition: "all 0.3s ease",
                  cursor: planInfo.canCreateResume ? "pointer" : "not-allowed",
                  opacity: planInfo.canCreateResume ? 1 : 0.6,
                }}
                styles={{
                  body: {
                    padding: "32px 24px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    height: "100%",
                  },
                }}
                onClick={planInfo.canCreateResume ? handleEnhanceExisting : undefined}
              >
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <div
                  style={{
                    backgroundColor: "#e6f7ff",
                    borderRadius: "50%",
                    width: "80px",
                    height: "80px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto",
                  }}
                >
                  <UploadOutlined style={{ fontSize: "36px", color: "#1890ff" }} />
                </div>

                <div>
                  <Title level={3} style={{ margin: "0 0 8px 0", color: "#1890ff" }}>
                    ENHANCE
                  </Title>
                  <Title level={4} style={{ margin: "0 0 16px 0", fontWeight: "600" }}>
                    Existing Resume
                  </Title>
                </div>

                <Text
                  style={{
                    fontSize: "15px",
                    color: "#666",
                    lineHeight: "1.5",
                    marginBottom: "24px",
                    display: "block",
                  }}
                >
                  Upload your current resume and get AI-powered improvements, formatting, and content optimization
                </Text>

                <div style={{ marginTop: "auto" }}>
                  <Button
                    type="default"
                    size="large"
                    icon={<UploadOutlined />}
                    disabled={!planInfo.canCreateResume}
                    style={{
                      borderRadius: "8px",
                      fontWeight: "600",
                      height: "48px",
                      paddingLeft: "24px",
                      paddingRight: "24px",
                      border: "2px solid #1890ff",
                      color: planInfo.canCreateResume ? "#1890ff" : "#999",
                      backgroundColor: "transparent",
                      transition: "all 0.3s ease",
                    }}
                    className="upload-btn"
                  >
                    {planInfo.canCreateResume ? "Upload File" : "Limit Reached"}
                  </Button>
                </div>

                <div style={{ marginTop: "16px" }}>
                  <Text style={{ fontSize: "12px", color: "#999" }}>Supports PDF, DOCX • Max 2MB</Text>
                </div>
              </Space>
            </Card>
            </Tooltip>
          </Col>

          {/* Create New Resume Card */}
          <Col xs={24} sm={24} md={12}>
            <Tooltip title={!planInfo.canCreateResume ? "You have reached your resume limit" : ""}>
              <Card
                hoverable={planInfo.canCreateResume}
                className="resume-choice-card create-card"
                style={{
                  height: "100%",
                  borderRadius: "16px",
                  border: "2px solid #e8e8e8",
                  background: planInfo.canCreateResume
                    ? "linear-gradient(135deg, #e6f7ff 0%, #f0f9ff 100%)"
                    : "#f5f5f5",
                  transition: "all 0.3s ease",
                  cursor: planInfo.canCreateResume ? "pointer" : "not-allowed",
                  position: "relative",
                  opacity: planInfo.canCreateResume ? 1 : 0.6,
                }}
                styles={{
                  body: {
                    padding: "32px 24px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    height: "100%",
                  },
                }}
                onClick={planInfo.canCreateResume ? handleCreateNew : undefined}
              >
              {/* New Feature Badge */}
              <div
                style={{
                  position: "absolute",
                  top: "-8px",
                  right: "16px",
                  backgroundColor: "#ff4d4f",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: "12px",
                  fontSize: "11px",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                NEW
              </div>

              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <div
                  style={{
                    backgroundColor: "#e6f7ff",
                    borderRadius: "50%",
                    width: "80px",
                    height: "80px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto",
                    border: "3px solid #1890ff",
                  }}
                >
                  <StarOutlined style={{ fontSize: "36px", color: "#1890ff" }} />
                </div>

                <div>
                  <Title level={3} style={{ margin: "0 0 8px 0", color: "#1890ff" }}>
                    CREATE NEW
                  </Title>
                  <Title level={4} style={{ margin: "0 0 16px 0", fontWeight: "600" }}>
                    From Scratch
                  </Title>
                </div>

                <Text
                  style={{
                    fontSize: "15px",
                    color: "#666",
                    lineHeight: "1.5",
                    marginBottom: "24px",
                    display: "block",
                  }}
                >
                  Start fresh with your target job role and get AI guidance to build a professional resume step by
                  step
                </Text>

                <div style={{ marginTop: "auto" }}>
                  <Button
                    type="primary"
                    size="large"
                    icon={<PlusOutlined />}
                    disabled={!planInfo.canCreateResume}
                    style={{
                      borderRadius: "8px",
                      fontWeight: "600",
                      height: "48px",
                      paddingLeft: "24px",
                      paddingRight: "24px",
                      backgroundColor: planInfo.canCreateResume ? "#1890ff" : undefined,
                      borderColor: planInfo.canCreateResume ? "#1890ff" : undefined,
                      boxShadow: planInfo.canCreateResume ? "0 2px 8px rgba(24, 144, 255, 0.3)" : "none",
                      transition: "all 0.3s ease",
                    }}
                    className="get-started-btn"
                  >
                    {planInfo.canCreateResume ? "Get Started" : "Limit Reached"}
                  </Button>
                </div>

                <div style={{ marginTop: "16px" }}>
                  <Text style={{ fontSize: "12px", color: "#999" }}>
                    Perfect for career changes & new graduates
                  </Text>
                </div>
              </Space>
            </Card>
            </Tooltip>
          </Col>
        </Row>
      </Content>
    </AppLayout>
  );
};

export default ResumeChoice;
